import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './index.module.scss';
import useInterval from '@hooks/useInterval';
import { I18n, Translate } from '@utils/i18n';
import { ReactComponent as InfoIcon } from './info.svg';
import { Logger } from '_clean-code/utils/logger';

interface VersionData {
  version: string;
  git: string;
  tag: string;
}

const CHECK_FOR_REFRESH = import.meta.env.VITE_APP_REFRESH ?? 'true';
const CHECK_FOR_REFRESH_INTERVAL = CHECK_FOR_REFRESH ? 1 * 60 * 1000 : null;

interface RefreshPageCheckerProps {
  classNames?: string;
}

function RefreshPageChecker(props: RefreshPageCheckerProps) {
  const [version, setVersion] = useState<VersionData | undefined>(undefined);
  const [showRefresh, setShowRefresh] = useState<boolean>(false);
  const [wasDismissed] = useState<boolean>(false);

  const check = async () => {
    try {
      const response = await fetch('/version.json', { cache: 'no-store' });
      const data = (await response.json()) as VersionData;

      if (!version) {
        setVersion(data);
        return;
      }

      if (data.version !== version.version) {
        setShowRefresh(true);
      }
    } catch (error) {
      Logger.error('RefreshPageChecker::check', error);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (CHECK_FOR_REFRESH_INTERVAL) {
      check();
    }
  }, []);

  useInterval(() => check(), CHECK_FOR_REFRESH_INTERVAL);

  const onRefresh = () => {
    window.location.reload();
  };

  if (!showRefresh) return null;

  if (wasDismissed)
    return (
      <div className={styles.dismissedMessage} onClick={onRefresh}>
        <Translate value="refresh_page_dismissed_message" />
      </div>
    );

  return (
    <div className={cn(styles.root, props.classNames)}>
      <div className={styles.content}>
        <InfoIcon className={styles.infoIcon} />
        <div className={styles.message}>
          {I18n.t('refresh_page_new_version')}
        </div>
        <div className={styles.refreshButton} onClick={onRefresh}>
          <Translate value="refresh_page_action" />
        </div>
      </div>
    </div>
  );
}

export default RefreshPageChecker;
